// src/routes/DashboardRoutes.js

import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import AdminDashboard from "../pages/AdminDashboard";
import authService from "../services/authService";

import Category from "../pages/category-module/Category";
import SubCategory from "../pages/category-module/SubCategory";
import ChildCategory from "../pages/category-module/ChildCategory";

import ReadProducts from "../pages/product-module/ReadProducts";
import CreateProduct from "../pages/product-module/CreateProduct";
import SellerReadProducts from "../pages/product-module/SellerReadProducts";
import SellerPendingReadProducts from "../pages/product-module/SellerPendingReadProducts";
import UpdateProduct from "../pages/product-module/UpdateProduct";
import CreateBulkProducts from "../pages/product-module/CreateBulkProduct";
import ImageGallery from "../pages/product-module/image-gallery/ImageGallery";
import ReadSoldProducts from "../pages/product-module/ReadSoldProducts";
import ReadOfferesProducts from "../pages/product-module/ReadOfferesProducts";
import ReadProductReviews from "../pages/product-module/product-reviews/ReadProductReviews";

import CreateBanner from "../pages/home-page-module/banner/CreateBanner";
import ReadBanners from "../pages/home-page-module/banner/ReadBanners";
import UpdateBanner from "../pages/home-page-module/banner/UpdateBanner";

import HomeSectionProducts from "../pages/Home-pagesetting-module/Homesectionproduct";
import HomeSectionCreate from "../pages/Home-pagesetting-module/Createhomesection";
import EditHomeSection from "../pages/Home-pagesetting-module/Edithomesection";

import Brand from "../pages/Brand-module/Brand";

import AdvertisementProducts from "../pages/Advertisement-module/Advertisementmanage";
import CreateAdvertisement from "../pages/Advertisement-module/CreateAdvertisement";

import AllOrders from "../pages/order-module/AllOrders";
import AllCanceledOrders from "../pages/order-module/AllCanceledOrders";
import AllDeliveredOrders from "../pages/order-module/AllDeliveredOrders";
import AllNewPendingOrders from "../pages/order-module/AllNewPendingOrders";
import AllPendingOrders from "../pages/order-module/AllPendingOrders";
import DisplayParticularOrderDetails from "../pages/order-module/DisplayParticularOrderDetails";
import ReadAllTransaction from "../pages/order-module/transaction-manage/ReadAllTransaction";

import ReadCustomers from "../pages/user-manage-module/ReadCustomers";
import ContactUsMessages from "../pages/contact-us-module/ContactUsMessages";
import CreateCustomer from "../pages/user-manage-module/CreateCustomer";

import CashOnDeliveryPayment from "../pages/payment-methods-module/CashOnDeliveryPayment";
import PaypalPayment from "../pages/payment-methods-module/PaypalPayment";
import PayUPayment from "../pages/payment-methods-module/PayUPayment";

import ShippingRule from "../pages/shipping-module/ShippingRule";

import ChangePassword from "../pages/profile-and-change-password/ChangePassword";
import ReadAndUpdateProfile from "../pages/profile-and-change-password/ReadAndUpdateProfile";

import AdminLogin from "../auth/AdminLogin";

import SettingRule from "../pages/Setting-module/Settingrule";


// ============================================================
// ADMIN PROTECTED ROUTE
// ============================================================

const AdminProtectedRoute = ({ children }) => {
  const user = authService.getCurrentUser();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};


// ============================================================
// PERMISSION ROUTE
// ============================================================

const PermissionRoute = ({
  module,
  action = "view",
  children,
}) => {
  const user = authService.getCurrentUser();

  // Not logged in
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check permission from API response
  const allowed = authService.hasPermission(module, action);

  console.log("ROUTE PERMISSION CHECK:", {
    module,
    action,
    allowed,
    userId: user?.id,
    role: user?.role?.name,
    roleSlug: user?.role?.slug,
    permissions: user?.permissions,
  });

  if (!allowed) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};


// ============================================================
// DASHBOARD ROUTES
// ============================================================

const DashboardRoutes = () => {
  return (
    <Routes>

      {/* ================================================== */}
      {/* ADMIN LOGIN */}
      {/* ================================================== */}

      <Route
        path="/login"
        element={<AdminLogin />}
      />


      {/* ================================================== */}
      {/* DASHBOARD */}
      {/* All authenticated admin roles can access dashboard */}
      {/* ================================================== */}

      <Route
        path="/dashboard"
        element={
          <AdminProtectedRoute>
            <AdminDashboard />
          </AdminProtectedRoute>
        }
      />


      {/* ================================================== */}
      {/* HOME PAGE */}
      {/* API MODULE: homepage */}
      {/* ================================================== */}

      <Route
        path="/slider/create"
        element={
          <PermissionRoute
            module="homepage"
            action="create"
          >
            <CreateBanner />
          </PermissionRoute>
        }
      />

      <Route
        path="/sliders"
        element={
          <PermissionRoute
            module="homepage"
            action="view"
          >
            <ReadBanners />
          </PermissionRoute>
        }
      />

      <Route
        path="/slider/edit/:id"
        element={
          <PermissionRoute
            module="homepage"
            action="update"
          >
            <UpdateBanner />
          </PermissionRoute>
        }
      />

      <Route
        path="/home-section-products"
        element={
          <PermissionRoute
            module="homepage"
            action="view"
          >
            <HomeSectionProducts />
          </PermissionRoute>
        }
      />

      <Route
        path="/create-home-section"
        element={
          <PermissionRoute
            module="homepage"
            action="create"
          >
            <HomeSectionCreate />
          </PermissionRoute>
        }
      />

      <Route
        path="/edit-home-section/:id"
        element={
          <PermissionRoute
            module="homepage"
            action="update"
          >
            <EditHomeSection />
          </PermissionRoute>
        }
      />


      {/* ================================================== */}
      {/* MARKETING */}
      {/* API MODULE: marketing */}
      {/* ================================================== */}

      <Route
        path="/advertise-section-products"
        element={
          <PermissionRoute
            module="marketing"
            action="view"
          >
            <AdvertisementProducts />
          </PermissionRoute>
        }
      />

      <Route
        path="/create-advertisement-section"
        element={
          <PermissionRoute
            module="marketing"
            action="create"
          >
            <CreateAdvertisement />
          </PermissionRoute>
        }
      />


      {/* ================================================== */}
      {/* CATALOG */}
      {/* API MODULE: catalog */}
      {/* ================================================== */}

      <Route
        path="/brands"
        element={
          <PermissionRoute
            module="catalog"
            action="view"
          >
            <Brand />
          </PermissionRoute>
        }
      />

      <Route
        path="/categories"
        element={
          <PermissionRoute
            module="catalog"
            action="view"
          >
            <Category />
          </PermissionRoute>
        }
      />

      <Route
        path="/sub-categories"
        element={
          <PermissionRoute
            module="catalog"
            action="view"
          >
            <SubCategory />
          </PermissionRoute>
        }
      />

      <Route
        path="/child-categories"
        element={
          <PermissionRoute
            module="catalog"
            action="view"
          >
            <ChildCategory />
          </PermissionRoute>
        }
      />


      {/* ================================================== */}
      {/* PRODUCTS */}
      {/* API MODULE: products */}
      {/* ================================================== */}

      <Route
        path="/all-products"
        element={
          <PermissionRoute
            module="products"
            action="view"
          >
            <ReadProducts />
          </PermissionRoute>
        }
      />

      <Route
        path="/product/create"
        element={
          <PermissionRoute
            module="products"
            action="create"
          >
            <CreateProduct />
          </PermissionRoute>
        }
      />

      <Route
        path="/active-products"
        element={
          <PermissionRoute
            module="products"
            action="view"
          >
            <SellerReadProducts />
          </PermissionRoute>
        }
      />

      <Route
        path="/inactive-products"
        element={
          <PermissionRoute
            module="products"
            action="view"
          >
            <SellerPendingReadProducts />
          </PermissionRoute>
        }
      />

      <Route
        path="/all-sold-products"
        element={
          <PermissionRoute
            module="products"
            action="view"
          >
            <ReadSoldProducts />
          </PermissionRoute>
        }
      />

      <Route
        path="/all-offers-products"
        element={
          <PermissionRoute
            module="products"
            action="view"
          >
            <ReadOfferesProducts />
          </PermissionRoute>
        }
      />

      <Route
        path="/products/edit/:id/"
        element={
          <PermissionRoute
            module="products"
            action="update"
          >
            <UpdateProduct />
          </PermissionRoute>
        }
      />

      <Route
        path="/product-image-gallery/:id/"
        element={
          <PermissionRoute
            module="products"
            action="view"
          >
            <ImageGallery />
          </PermissionRoute>
        }
      />

      <Route
        path="/product-review/:productId"
        element={
          <PermissionRoute
            module="products"
            action="view"
          >
            <ReadProductReviews />
          </PermissionRoute>
        }
      />

      <Route
        path="/product/create-bulk"
        element={
          <PermissionRoute
            module="products"
            action="import"
          >
            <CreateBulkProducts />
          </PermissionRoute>
        }
      />


      {/* ================================================== */}
      {/* ORDERS */}
      {/* API MODULE: orders */}
      {/* ================================================== */}

      <Route
        path="/all-orders"
        element={
          <PermissionRoute
            module="orders"
            action="view"
          >
            <AllOrders />
          </PermissionRoute>
        }
      />

      <Route
        path="/new-orders"
        element={
          <PermissionRoute
            module="orders"
            action="view"
          >
            <AllNewPendingOrders />
          </PermissionRoute>
        }
      />

      <Route
        path="/delivered-orders"
        element={
          <PermissionRoute
            module="orders"
            action="view"
          >
            <AllDeliveredOrders />
          </PermissionRoute>
        }
      />

      <Route
        path="/cancelled-orders"
        element={
          <PermissionRoute
            module="orders"
            action="view"
          >
            <AllCanceledOrders />
          </PermissionRoute>
        }
      />

      <Route
        path="/pending-orders"
        element={
          <PermissionRoute
            module="orders"
            action="view"
          >
            <AllPendingOrders />
          </PermissionRoute>
        }
      />

      <Route
        path="/order/:id"
        element={
          <PermissionRoute
            module="orders"
            action="view"
          >
            <DisplayParticularOrderDetails />
          </PermissionRoute>
        }
      />


      {/* ================================================== */}
      {/* PAYMENTS */}
      {/* API MODULE: payments */}
      {/* ================================================== */}

      <Route
        path="/cod-settings"
        element={
          <PermissionRoute
            module="payments"
            action="view"
          >
            <CashOnDeliveryPayment />
          </PermissionRoute>
        }
      />

      <Route
        path="/paypal-settings"
        element={
          <PermissionRoute
            module="payments"
            action="view"
          >
            <PaypalPayment />
          </PermissionRoute>
        }
      />

      <Route
        path="/payu-settings"
        element={
          <PermissionRoute
            module="payments"
            action="view"
          >
            <PayUPayment />
          </PermissionRoute>
        }
      />

      <Route
        path="/transactions"
        element={
          <PermissionRoute
            module="payments"
            action="view"
          >
            <ReadAllTransaction />
          </PermissionRoute>
        }
      />


      {/* ================================================== */}
      {/* USERS */}
      {/* API MODULE: users */}
      {/* ================================================== */}

      <Route
        path="/user-management"
        element={
          <PermissionRoute
            module="users"
            action="view"
          >
            <ReadCustomers />
          </PermissionRoute>
        }
      />

      <Route
        path="/contact-us-list"
        element={
          <PermissionRoute
            module="users"
            action="view"
          >
            <ContactUsMessages />
          </PermissionRoute>
        }
      />

      <Route
        path="/customer/create"
        element={
          <PermissionRoute
            module="users"
            action="create"
          >
            <CreateCustomer />
          </PermissionRoute>
        }
      />


      {/* ================================================== */}
      {/* SHIPPING */}
      {/* API MODULE: shipping */}
      {/* ================================================== */}

      <Route
        path="/shipping-rule"
        element={
          <PermissionRoute
            module="shipping"
            action="view"
          >
            <ShippingRule />
          </PermissionRoute>
        }
      />


      {/* ================================================== */}
      {/* SETTINGS */}
      {/* No API permission module available */}
      {/* Only logged-in users can access */}
      {/* ================================================== */}

      <Route
        path="/setting-rule"
        element={
          <AdminProtectedRoute>
            <SettingRule />
          </AdminProtectedRoute>
        }
      />

      <Route
        path="/change-password"
        element={
          <AdminProtectedRoute>
            <ChangePassword />
          </AdminProtectedRoute>
        }
      />

      <Route
        path="/profile"
        element={
          <AdminProtectedRoute>
            <ReadAndUpdateProfile />
          </AdminProtectedRoute>
        }
      />


      {/* ================================================== */}
      {/* UNKNOWN ROUTE */}
      {/* ================================================== */}

      <Route
        path="*"
        element={
          <Navigate
            to="/admin/dashboard"
            replace
          />
        }
      />

    </Routes>
  );
};

export default DashboardRoutes;